import { useEffect, useState } from 'react';
import { Smartphone, Star, Search, RefreshCw, Loader2, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import { storiesApi } from '../../api/client';

// ── Sub-components ────────────────────────────────────────────

function StoryAvatar({ isDefault }) {
  if (isDefault) {
    return (
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: '#fef3c7' }}
      >
        <Star size={14} fill="#f59e0b" color="#f59e0b" />
      </div>
    );
  }
  return (
    <div
      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ background: 'var(--brand-50)' }}
    >
      <BookOpen size={14} style={{ color: 'var(--brand-primary)' }} />
    </div>
  );
}

function ToggleKnob({ isMobile, isToggling }) {
  if (isToggling) {
    return (
      <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', display: 'flex' }}>
        <Loader2 size={10} className="animate-spin" style={{ color: isMobile ? '#fff' : '#94a3b8' }} />
      </span>
    );
  }
  return (
    <span
      style={{
        position: 'absolute',
        top: 2,
        left: isMobile ? 18 : 2,
        width: 16,
        height: 16,
        borderRadius: '50%',
        background: '#ffffff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        transition: 'left 0.2s',
      }}
    />
  );
}

function DefaultCell({ story, isSettingDefault, onSetDefault }) {
  if (story.isDefault) {
    return (
      <div className="flex items-center gap-1.5">
        <Star size={13} fill="#f59e0b" color="#f59e0b" />
        <span className="text-xs font-semibold" style={{ color: '#d97706' }}>Default</span>
      </div>
    );
  }
  return (
    <button
      onClick={() => onSetDefault(story)}
      disabled={isSettingDefault}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
      style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        color: '#64748b',
        cursor: isSettingDefault ? 'wait' : 'pointer',
      }}
      onMouseEnter={(e) => {
        if (isSettingDefault) return;
        e.currentTarget.style.borderColor = 'var(--brand-primary)';
        e.currentTarget.style.color = 'var(--brand-primary)';
        e.currentTarget.style.background = 'var(--brand-50)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#e2e8f0';
        e.currentTarget.style.color = '#64748b';
        e.currentTarget.style.background = '#f8fafc';
      }}
    >
      {isSettingDefault ? <Loader2 size={11} className="animate-spin" /> : <Star size={11} />}
      Set as Default
    </button>
  );
}

function StoryRow({ story, isLast, toggling, settingDefault, onMobileToggle, onSetDefault }) {
  const isMobile        = !!story.mobileAvailable;
  const isDefault       = !!story.isDefault;
  const isTogglingThis  = !!toggling[story.id];
  const isSettingThis   = settingDefault === story.id;

  return (
    <div
      className="grid items-center px-4 py-3 hover:bg-slate-50 transition-colors"
      style={{
        gridTemplateColumns: '1fr 160px 160px',
        borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
        background: isDefault ? '#fffbeb' : undefined,
      }}
    >
      {/* Story name */}
      <div className="flex items-center gap-2.5 min-w-0 pr-4">
        <StoryAvatar isDefault={isDefault} />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate" style={{ color: '#1e293b' }} title={story.title}>
            {story.title || 'Untitled Story'}
          </p>
          {isDefault && (
            <p className="text-[10px] font-semibold mt-0.5" style={{ color: '#d97706' }}>Default story</p>
          )}
        </div>
      </div>

      {/* Mobile toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => !isTogglingThis && onMobileToggle(story)}
          disabled={isTogglingThis}
          aria-label={isMobile ? 'Disable on mobile' : 'Enable on mobile'}
          className="relative flex-shrink-0"
          style={{
            width: 36,
            height: 20,
            borderRadius: 10,
            background: isMobile ? 'var(--brand-primary)' : '#e2e8f0',
            border: 'none',
            cursor: isTogglingThis ? 'wait' : 'pointer',
            outline: 'none',
            opacity: isTogglingThis ? 0.6 : 1,
            transition: 'background 0.2s',
            padding: 0,
          }}
        >
          <ToggleKnob isMobile={isMobile} isToggling={isTogglingThis} />
        </button>
        <span className="text-xs font-medium" style={{ color: isMobile ? 'var(--brand-primary)' : '#94a3b8' }}>
          {isMobile ? 'On' : 'Off'}
        </span>
      </div>

      {/* Default */}
      <DefaultCell story={story} isSettingDefault={isSettingThis} onSetDefault={onSetDefault} />
    </div>
  );
}

function TableContent({ loading, filtered, search, toggling, settingDefault, onMobileToggle, onSetDefault }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-14 rounded-xl" style={{ border: '1px solid #e2e8f0' }}>
        <Loader2 size={22} className="animate-spin" style={{ color: 'var(--brand-primary)' }} />
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="rounded-xl p-10 text-center" style={{ border: '1px dashed #e2e8f0', background: '#fafafa' }}>
        <BookOpen size={32} className="mx-auto mb-2" style={{ color: '#cbd5e1' }} />
        <p className="text-sm font-medium" style={{ color: '#94a3b8' }}>
          {search ? 'No stories match your search' : 'No stories found'}
        </p>
        <p className="text-xs mt-1" style={{ color: '#cbd5e1' }}>
          {search ? 'Try a different keyword.' : 'Publish a story first to configure SDK access.'}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #e2e8f0' }}>
      <div
        className="grid text-[11px] font-semibold uppercase tracking-wider px-4 py-2.5"
        style={{
          gridTemplateColumns: '1fr 160px 160px',
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          color: '#94a3b8',
        }}
      >
        <span>Story Name</span>
        <span>Available on Mobile</span>
        <span>Default Story</span>
      </div>

      {filtered.map((story, idx) => (
        <StoryRow
          key={story.id}
          story={story}
          isLast={idx === filtered.length - 1}
          toggling={toggling}
          settingDefault={settingDefault}
          onMobileToggle={onMobileToggle}
          onSetDefault={onSetDefault}
        />
      ))}
    </div>
  );
}

// ── Pagination bar ────────────────────────────────────────────

function Pagination({ meta, page, onPage }) {
  if (!meta || meta.total <= meta.limit) return null;
  const totalPages = Math.ceil(meta.total / meta.limit);
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <div className="flex items-center justify-between mt-3 px-1">
      <span className="text-xs" style={{ color: '#94a3b8' }}>
        {((page - 1) * meta.limit) + 1}–{Math.min(page * meta.limit, meta.total)} of {meta.total}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPage(page - 1)}
          disabled={page === 1}
          className="px-2 py-1 rounded text-xs font-medium transition-colors"
          style={{
            border: '1px solid #e2e8f0',
            background: page === 1 ? '#f8fafc' : '#fff',
            color: page === 1 ? '#cbd5e1' : '#475569',
            cursor: page === 1 ? 'default' : 'pointer',
          }}
        >
          ‹ Prev
        </button>

        {pages.map((p) => (
          <button
            key={p}
            onClick={() => onPage(p)}
            className="w-7 h-7 rounded text-xs font-medium transition-colors"
            style={{
              border: '1px solid',
              borderColor: p === page ? 'var(--brand-primary)' : '#e2e8f0',
              background: p === page ? 'var(--brand-primary)' : '#fff',
              color: p === page ? '#fff' : '#475569',
              cursor: p === page ? 'default' : 'pointer',
            }}
          >
            {p}
          </button>
        ))}

        <button
          onClick={() => onPage(page + 1)}
          disabled={page === totalPages}
          className="px-2 py-1 rounded text-xs font-medium transition-colors"
          style={{
            border: '1px solid #e2e8f0',
            background: page === totalPages ? '#f8fafc' : '#fff',
            color: page === totalPages ? '#cbd5e1' : '#475569',
            cursor: page === totalPages ? 'default' : 'pointer',
          }}
        >
          Next ›
        </button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

const PAGE_LIMIT = 10;

export default function SdkConfigTab() {
  const [stories, setStories]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [toggling, setToggling]             = useState({});
  const [settingDefault, setSettingDefault] = useState(null);
  const [search, setSearch]                 = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage]                     = useState(1);
  const [meta, setMeta]                     = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStories(1); }, []);

  // Debounce search — 400 ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  async function loadStories(p = page) {
    setLoading(true);
    try {
      const result = await storiesApi.list(p, PAGE_LIMIT, { status: 'published' });
      setStories(result.data || []);
      if (result.meta) setMeta(result.meta);
      setPage(p);
    } catch {
      toast.error('Failed to load stories');
    } finally {
      setLoading(false);
    }
  }

  async function handleMobileToggle(story) {
    const newVal = !story.mobileAvailable;
    setToggling((p) => ({ ...p, [story.id]: true }));
    try {
      await storiesApi.setMobileAvailability([story.id], newVal);
      setStories((prev) =>
        prev.map((s) => s.id === story.id ? { ...s, mobileAvailable: newVal } : s)
      );
      loadStories(page);
      toast.success(newVal ? `"${story.title}" enabled on mobile` : `"${story.title}" disabled on mobile`);
    } catch (err) {
      toast.error(err.message || 'Failed to update mobile availability');
    } finally {
      setToggling((p) => ({ ...p, [story.id]: false }));
    }
  }

  async function handleSetDefault(story) {
    setSettingDefault(story.id);
    try {
      await storiesApi.setDefault(story.id);
      setStories((prev) => prev.map((s) => ({ ...s, isDefault: s.id === story.id })));
      loadStories(page);
      toast.success(`"${story.title}" is now the default story`);
    } catch (err) {
      toast.error(err.message || 'Failed to set default story');
    } finally {
      setSettingDefault(null);
    }
  }

  const published   = stories.filter((s) => s.status === 'published');
  const filtered    = published.filter((s) => (s.title || '').toLowerCase().includes(debouncedSearch.toLowerCase()));
  const mobileCount = published.filter((s) => s.mobileAvailable).length;

  return (
    <div>
      {/* Info banner */}
      <div
        className="rounded-lg p-3 mb-5 text-xs flex items-start gap-2"
        style={{ background: 'var(--brand-50)', color: 'var(--brand-800)', border: '1px solid var(--brand-200)' }}
      >
        <Smartphone size={14} className="mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-semibold mb-0.5">Mobile SDK Configuration</div>
          <div style={{ opacity: 0.85 }}>
            Control which stories appear in your mobile app and designate the default story shown to first-time viewers.
            Use the API key and access token from your account settings to authenticate SDK requests.
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold" style={{ color: '#1e293b' }}>Stories</h3>
          {!loading && (
            <div className="flex items-center gap-1.5">
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'var(--brand-50)', color: 'var(--brand-primary)' }}>
                {published.length} published
              </span>
              {mobileCount > 0 && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: '#dcfce7', color: '#16a34a' }}>
                  {mobileCount} on mobile
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#94a3b8' }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search stories…"
              className="pl-7 pr-3 py-1.5 rounded-lg text-xs"
              style={{ border: '1px solid #e2e8f0', background: '#ffffff', color: '#1e293b', width: 180, outline: 'none' }}
            />
          </div>
          <button
            onClick={() => loadStories(1)}
            disabled={loading}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            title="Refresh"
          >
            <RefreshCw size={13} style={{ color: '#94a3b8' }} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <TableContent
        loading={loading}
        filtered={filtered}
        search={debouncedSearch}
        toggling={toggling}
        settingDefault={settingDefault}
        onMobileToggle={handleMobileToggle}
        onSetDefault={handleSetDefault}
      />

      <Pagination meta={meta} page={page} onPage={loadStories} />
    </div>
  );
}
